import { useState } from "react";
import { API } from "../api";
import { setDeviceExcluded, isDeviceExcluded, getAnonId } from "../lib/analytics";
import {
  BarChart3, Users, Activity, Eye, Lock, Loader2, CalendarRange, Sun, UserCheck,
  ShieldOff, Shield, Repeat, Flame, TrendingUp, TrendingDown, RefreshCw, BookOpen,
  Headphones, Sparkles, GraduationCap, Music, Brain, Bookmark, Search, Zap, Minus,
  Smartphone, Download as DownloadIcon,
} from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const RANGES = [
  { label: "Today", days: 1 },
  { label: "Week", days: 7 },
  { label: "Month", days: 30 },
  { label: "3 months", days: 90 },
  { label: "6 months", days: 180 },
  { label: "Year", days: 365 },
  { label: "All time", days: 0 },
];

const PLATFORM_FILTERS = [
  { key: "", label: "All platforms" },
  { key: "ios", label: "iPhone app" },
  { key: "android", label: "Android app" },
  { key: "web", label: "Web browsers" },
];

const ENGAGE_ICONS = {
  chapter_complete: BookOpen,
  audio_play: Headphones,
  study_open: Sparkles,
  explain_verse: GraduationCap,
  explain_chapter: GraduationCap,
  atmosphere_play: Music,
  quiz_complete: Brain,
  verse_saved: Bookmark,
  word_study_search: Search,
};

// ▲ / ▼ vs the previous equal period
function Trend({ pct, compareLabel }) {
  if (pct === null || pct === undefined) return null;
  const up = pct > 0;
  const flat = pct === 0;
  const color = flat ? "text-muted-foreground" : up ? "text-emerald-500" : "text-red-400";
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  return (
    <span className={`font-ui inline-flex items-center gap-1 text-xs font-bold ${color}`} title={compareLabel}>
      <Icon className="h-3.5 w-3.5" />
      {flat ? "flat" : `${up ? "+" : ""}${pct}%`}
    </span>
  );
}

function Kpi({ icon: Icon, label, value, sub, pct, compareLabel, highlight }) {
  return (
    <div
      className={`rounded-2xl border p-5 ${highlight ? "border-primary/40 bg-primary/5" : "border-border bg-card"}`}
      data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Icon className={`h-4 w-4 ${highlight ? "text-primary" : ""}`} />
          <span className="font-ui text-xs font-semibold uppercase tracking-wide">{label}</span>
        </div>
        <Trend pct={pct} compareLabel={compareLabel} />
      </div>
      <p className="font-serif-display mt-2 text-3xl">{value?.toLocaleString?.() ?? value}</p>
      {sub && <p className="font-ui mt-0.5 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function SourceBars({ rows, testid }) {
  const total = rows.reduce((a, s) => a + s.sessions, 0) || 1;
  return (
    <div className="space-y-3" data-testid={testid}>
      {rows.map((s) => {
        const pct = Math.round((s.sessions / total) * 100);
        return (
          <div key={s.source} data-testid={`source-${s.source.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
            <div className="mb-1 flex items-center justify-between">
              <span className="font-ui text-sm">{s.source}</span>
              <span className="font-ui text-sm font-bold text-primary">{s.sessions.toLocaleString()} visits · {pct}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminStats() {
  const [key, setKey] = useState(localStorage.getItem("fw_admin_key") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [range, setRange] = useState(30);
  const [excluded, setExcluded] = useState(isDeviceExcluded());
  const [appOnly, setAppOnly] = useState(true);
  const [platform, setPlatform] = useState("");
  const [showDayTable, setShowDayTable] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = async (k = key, days = range, app = appOnly, plat = platform) => {
    setLoading(true); setErr("");
    try {
      const res = await fetch(`${API}/analytics/stats?days=${days}&app_only=${app}&platform=${plat}`, { headers: { "X-Admin-Key": k } });
      if (res.status === 401) { setErr("Wrong passcode."); setLoading(false); return; }
      const j = await res.json();
      localStorage.setItem("fw_admin_key", k);
      setData(j);
      setLastUpdated(new Date());
    } catch {
      setErr("Could not load stats.");
    }
    setLoading(false);
  };

  const excludeDevice = async (k, v) => {
    try {
      await fetch(`${API}/analytics/exclude`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Key": k },
        body: JSON.stringify({ anon_id: getAnonId(), excluded: v }),
      });
    } catch { /* ignore */ }
  };

  // unlock from the passcode screen: auto-exclude this device ONCE (first view),
  // but never override a manual choice the owner made later.
  const unlock = async (k = key) => {
    if (!localStorage.getItem("fw_internal_decided")) {
      setDeviceExcluded(true); setExcluded(true);
      await excludeDevice(k, true);
      localStorage.setItem("fw_internal_decided", "1");
    }
    load(k, range);
  };

  const changeRange = (days) => { setRange(days); load(key, days); };

  const toggleExcluded = async () => {
    const v = !excluded;
    setDeviceExcluded(v); setExcluded(v);
    localStorage.setItem("fw_internal_decided", "1");
    await excludeDevice(key, v);
    load(key, range);
  };

  const toggleAppOnly = () => {
    const v = !appOnly;
    setAppOnly(v);
    load(key, range, v);
  };

  const changePlatform = (p) => {
    setPlatform(p);
    load(key, range, appOnly, p);
  };

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <div className="w-full max-w-sm rounded-3xl border border-border bg-card p-8">
          <div className="mb-4 flex items-center gap-2 text-primary">
            <Lock className="h-5 w-5" />
            <h1 className="font-serif-display text-2xl">Clarity Bible Stats</h1>
          </div>
          <p className="font-ui mb-5 text-sm text-muted-foreground">Enter your admin passcode to view usage analytics.</p>
          <input
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && unlock()}
            placeholder="Admin passcode"
            data-testid="admin-key-input"
            className="font-ui mb-3 w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary"
          />
          {err && <p className="font-ui mb-3 text-sm text-destructive" data-testid="admin-error">{err}</p>}
          <button
            onClick={() => unlock()}
            disabled={loading || !key}
            data-testid="admin-unlock"
            className="font-ui flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-bold text-primary-foreground disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "View stats"}
          </button>
        </div>
      </div>
    );
  }

  const rangeLabel = RANGES.find((r) => r.days === range)?.label || `${range} days`;
  const compareLabel = range === 1 ? "vs yesterday" : `vs previous ${rangeLabel.toLowerCase()}`;
  const k = data.kpis || {};
  const t = k.today || {};
  const todayUsersPct = t.users_yesterday ? Math.round(((t.users - t.users_yesterday) / t.users_yesterday) * 100) : null;
  const todayChaptersPct = t.chapters_yesterday ? Math.round(((t.chapters - t.chapters_yesterday) / t.chapters_yesterday) * 100) : null;

  return (
    <div className="min-h-screen bg-background px-6 py-10" data-testid="admin-stats-page">
      {/* ---- Header ---- */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif-display text-4xl">Usage Analytics</h1>
          <p className="font-ui mt-1 text-sm text-muted-foreground">Collecting since {data.since}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              onClick={toggleAppOnly}
              data-testid="app-only-toggle"
              title="App users only removes /download landing-page visitors and store redirects from every core number. Download traffic still has its own section below."
              className={`font-ui inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                appOnly ? "border-primary/40 bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted"
              }`}
            >
              <Smartphone className="h-3.5 w-3.5" />
              {appOnly ? "App users only" : "Including download traffic"}
            </button>
            <button
              onClick={toggleExcluded}
              data-testid="exclude-device-toggle"
              className={`font-ui inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                excluded ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-500" : "border-border text-muted-foreground hover:bg-muted"
              }`}
            >
              {excluded ? <ShieldOff className="h-3.5 w-3.5" /> : <Shield className="h-3.5 w-3.5" />}
              {excluded ? "This device isn’t counted" : "Count this device"}
            </button>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex flex-wrap items-center justify-end gap-1.5" data-testid="range-toggle">
            <CalendarRange className="mr-1 h-4 w-4 text-muted-foreground" />
            {RANGES.map((r) => (
              <button
                key={r.days}
                onClick={() => changeRange(r.days)}
                data-testid={`range-${r.days}`}
                className={`font-ui rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${range === r.days ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:bg-muted"}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {lastUpdated && (
              <span className="font-ui text-xs text-muted-foreground" data-testid="last-updated">
                Updated {lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </span>
            )}
            <button
              onClick={() => load()}
              disabled={loading}
              data-testid="refresh-stats"
              className="font-ui inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-muted disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* ---- Platform filter ---- */}
      <div className="mb-4 flex flex-wrap items-center gap-1.5" data-testid="platform-filter">
        <Smartphone className="mr-1 h-4 w-4 text-muted-foreground" />
        {PLATFORM_FILTERS.map((p) => (
          <button
            key={p.key}
            onClick={() => changePlatform(p.key)}
            data-testid={`platform-filter-${p.key || "all"}`}
            className={`font-ui rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${platform === p.key ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:bg-muted"}`}
          >
            {p.label}
          </button>
        ))}
        {platform && (
          <span className="font-ui ml-1 text-xs text-muted-foreground">
            All numbers below are {PLATFORM_FILTERS.find((p) => p.key === platform)?.label} only
          </span>
        )}
      </div>

      {/* ---- Spike callouts ---- */}
      {data.spikes?.length > 0 && (
        <div className="mb-6 space-y-2" data-testid="spike-callouts">
          {data.spikes.map((s, i) => (
            <div key={`${s.day}-${s.metric}-${i}`} className="font-ui flex items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm" data-testid={`spike-${i}`}>
              <Zap className="h-4 w-4 shrink-0 text-amber-500" />
              <span>
                <span className="font-bold">Spike:</span> {s.value.toLocaleString()} {s.metric} on {s.day}
                {s.factor ? <span className="text-muted-foreground"> — {s.factor}× the 7-day average ({s.avg})</span> : <span className="text-muted-foreground"> — up from a quiet week</span>}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ---- North Star: chapters finished, all time ---- */}
      <div className="mb-6 rounded-3xl border border-primary/40 bg-gradient-to-br from-primary/10 to-transparent p-6" data-testid="north-star">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-primary">
              <BookOpen className="h-5 w-5" />
              <span className="font-ui text-xs font-semibold uppercase tracking-wide">Chapters of the Bible finished · all time</span>
            </div>
            <p className="font-serif-display mt-1 text-6xl" data-testid="chapters-all-time">{(data.chapters_all_time ?? 0).toLocaleString()}</p>
          </div>
          <div className="flex gap-8 text-right">
            <div>
              <p className="font-ui text-xs font-semibold uppercase tracking-wide text-muted-foreground">Today</p>
              <p className="font-serif-display text-3xl">{(t.chapters ?? 0).toLocaleString()}</p>
              <Trend pct={todayChaptersPct} compareLabel="vs yesterday" />
            </div>
            <div>
              <p className="font-ui text-xs font-semibold uppercase tracking-wide text-muted-foreground">Yesterday</p>
              <p className="font-serif-display text-3xl">{(t.chapters_yesterday ?? 0).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Core KPIs with trends ---- */}
      <p className="font-ui mb-2 text-xs font-semibold uppercase tracking-wide text-primary">
        Key numbers · {rangeLabel.toLowerCase()} {range > 0 && <span className="normal-case text-muted-foreground">({compareLabel})</span>}
      </p>
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi icon={BookOpen} label="Chapters read" value={k.chapters?.current ?? 0} pct={k.chapters?.pct} compareLabel={compareLabel}
             sub={k.chapters?.prev != null ? `${k.chapters.prev.toLocaleString()} previous period` : "all time"} highlight />
        <Kpi icon={Users} label="Active users" value={k.active_users?.current ?? 0} pct={k.active_users?.pct} compareLabel={compareLabel}
             sub={k.active_users?.prev != null ? `${k.active_users.prev.toLocaleString()} previous period` : "unique devices"} />
        <Kpi icon={Flame} label="Loyal users" value={k.loyal?.total ?? 0} pct={k.loyal?.pct} compareLabel={`active loyal ${compareLabel}`}
             sub={`all-time (4+ days) · ${(k.loyal?.current ?? 0).toLocaleString()} active ${range === 0 ? "overall" : `this ${rangeLabel.toLowerCase()}`}`} />
        <Kpi icon={Sun} label="Users today" value={t.users ?? 0} pct={todayUsersPct} compareLabel="vs yesterday"
             sub={`${(t.users_yesterday ?? 0).toLocaleString()} yesterday`} />
      </div>

      {/* ---- Daily chart: users + chapters ---- */}
      <div className="mb-6 rounded-2xl border border-border bg-card p-5">
        <h2 className="font-ui mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Daily active users & chapters read · {rangeLabel}</h2>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={data.daily}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(d) => d?.slice(5)} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
            <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="users" fill="#E0B85C" radius={[6, 6, 0, 0]} name="Active users" />
            <Line type="monotone" dataKey="chapters" stroke="#5E9A4E" strokeWidth={2.5} dot={false} name="Chapters read" />
          </ComposedChart>
        </ResponsiveContainer>
        <button
          onClick={() => setShowDayTable((v) => !v)}
          data-testid="day-table-toggle"
          className="font-ui mt-3 inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted"
        >
          <CalendarRange className="h-3.5 w-3.5" />
          {showDayTable ? "Hide day-by-day history" : "Show day-by-day history"}
        </button>
        {showDayTable && (
          <div className="mt-4 max-h-80 overflow-y-auto rounded-xl border border-border" data-testid="day-table">
            <table className="font-ui w-full text-sm">
              <thead className="sticky top-0 bg-card">
                <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2.5">Date</th>
                  <th className="px-4 py-2.5 text-right">Active users</th>
                  <th className="px-4 py-2.5 text-right">Chapters read</th>
                  <th className="px-4 py-2.5 text-right">New devices</th>
                </tr>
              </thead>
              <tbody>
                {[...data.daily].reverse().map((d) => (
                  <tr key={d.day} className="border-b border-border/50 last:border-0" data-testid={`day-row-${d.day}`}>
                    <td className="px-4 py-2">{d.day}</td>
                    <td className="px-4 py-2 text-right font-bold text-primary">{d.users.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right">{d.chapters.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right text-muted-foreground">{(d.new_devices ?? 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ---- Feature engagement ---- */}
      <p className="font-ui mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Feature engagement · {rangeLabel.toLowerCase()}</p>
      <div className="mb-2 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="engagement-grid">
        {(data.engagement || []).map((e) => {
          const Icon = ENGAGE_ICONS[e.key] || Activity;
          return (
            <div key={e.key} className="rounded-2xl border border-border bg-card p-4" data-testid={`engage-${e.key}`}>
              <div className="flex items-center justify-between gap-1">
                <Icon className="h-4 w-4 text-primary" />
                <Trend pct={e.pct} compareLabel={compareLabel} />
              </div>
              <p className="font-serif-display mt-1.5 text-2xl">{e.count.toLocaleString()}</p>
              <p className="font-ui text-xs text-muted-foreground">{e.label}</p>
            </div>
          );
        })}
      </div>
      <p className="font-ui mb-6 text-xs text-muted-foreground">Quizzes count once per fully completed quiz — answer taps aren’t counted.</p>

      <div className="grid gap-8 lg:grid-cols-2">
        {/* ---- Most used features (kept) ---- */}
        <div className="rounded-2xl border border-border bg-card p-5">
          <h2 className="font-ui mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Top features used · {rangeLabel}</h2>
          <div className="space-y-2" data-testid="top-events">
            {data.top_events.map((e) => (
              <div key={e.event} className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
                <span className="font-ui text-sm">{e.event}</span>
                <span className="font-ui text-sm font-bold text-primary">{e.count.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <h2 className="font-ui mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Most-visited screens · {rangeLabel}</h2>
          <div className="space-y-2" data-testid="top-screens">
            {data.top_screens.map((s) => (
              <div key={s.path} className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
                <span className="font-ui truncate text-sm">{s.path}</span>
                <span className="font-ui text-sm font-bold text-primary">{s.count.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ---- Retention ---- */}
      {data.retention && (
        <div className="mt-10" data-testid="retention-section">
          <p className="font-ui mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Retention · are people coming back?</p>
          <div className="mb-3 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Kpi icon={Repeat} label="Repeat visitors" value={`${data.retention.repeat_rate}%`}
                 sub={`${data.retention.repeat_devices} of ${data.retention.total_devices} came back 2+ days`} />
            <Kpi icon={Flame} label="Loyal users" value={data.retention.loyal_devices} sub="active on 4+ separate days" />
            <Kpi icon={TrendingUp} label="Stickiness" value={`${data.retention.stickiness}%`}
                 sub={`${data.retention.dau} today / ${data.retention.mau} this month`} />
            <Kpi icon={CalendarRange} label="Avg. days/user" value={data.retention.avg_active_days} sub="distinct days each device read" />
          </div>
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="retention-buckets">
            <h2 className="font-ui mb-1 text-sm font-semibold uppercase tracking-wide text-muted-foreground">How often people return</h2>
            <p className="font-ui mb-4 text-xs text-muted-foreground">Anonymous devices grouped by how many separate days they've opened the app. No personal data stored.</p>
            {(() => {
              const b = data.retention.buckets;
              const rows = [
                { key: "one", label: "Just once (1 day)", count: b.one, color: "#9CA3AF" },
                { key: "two_three", label: "Coming back (2–3 days)", count: b.two_three, color: "#E0B85C" },
                { key: "four_seven", label: "Regulars (4–7 days)", count: b.four_seven, color: "#86B97A" },
                { key: "eight_plus", label: "Devoted (8+ days)", count: b.eight_plus, color: "#5E9A4E" },
              ];
              const total = rows.reduce((a, r) => a + r.count, 0) || 1;
              return (
                <div className="space-y-3" data-testid="retention-bucket-list">
                  {rows.map((r) => {
                    const pct = Math.round((r.count / total) * 100);
                    return (
                      <div key={r.key} data-testid={`retention-bucket-${r.key}`}>
                        <div className="mb-1 flex items-center justify-between">
                          <span className="font-ui text-sm">{r.label}</span>
                          <span className="font-ui text-sm font-bold text-primary">{r.count.toLocaleString()} · {pct}%</span>
                        </div>
                        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: r.color }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ---- How app users arrived ---- */}
      {data.sources_app?.length > 0 && (
        <div className="mt-10 rounded-2xl border border-border bg-card p-5" data-testid="sources-app-panel">
          <h2 className="font-ui mb-1 text-sm font-semibold uppercase tracking-wide text-primary">How app users arrived · {rangeLabel}</h2>
          <p className="font-ui mb-4 text-xs text-muted-foreground">
            <span className="font-semibold">“Direct”</span> = opened the installed app, typed the address, or tapped a bookmark —
            App Store / Play Store installs show up here as Direct app opens. Download-page traffic is shown separately below.
          </p>
          <SourceBars rows={data.sources_app} testid="sources-app-list" />
        </div>
      )}

      {/* ---- Download page: its own world ---- */}
      {data.download && (
        <div className="mt-10 rounded-2xl border border-dashed border-border bg-card/50 p-5" data-testid="download-section">
          <div className="mb-1 flex items-center gap-2 text-muted-foreground">
            <DownloadIcon className="h-4 w-4" />
            <h2 className="font-ui text-sm font-semibold uppercase tracking-wide">Download landing page · {rangeLabel}</h2>
          </div>
          <p className="font-ui mb-4 text-xs text-muted-foreground">Ad-driven store redirects — kept separate from app usage above.</p>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="download-funnel">
            <Kpi icon={Eye} label="Page views" value={data.download.views} sub="visited /download" />
            <Kpi icon={UserCheck} label="Store clicks" value={data.download.clicks} sub="tapped download" />
            <Kpi icon={TrendingUp} label="Click rate" value={`${data.download.ctr}%`} sub="views → clicked" />
            <Kpi icon={BarChart3} label="App Store" value={data.download.appstore_clicks} sub={data.download.playstore_clicks ? `+${data.download.playstore_clicks} Play` : "Apple clicks"} />
          </div>
          {data.sources_download?.length > 0 && (
            <div className="mt-5">
              <h3 className="font-ui mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Where download visitors came from</h3>
              <SourceBars rows={data.sources_download} testid="sources-download-list" />
            </div>
          )}
        </div>
      )}

      {/* ---- Platforms ---- */}
      {data.platforms?.length > 0 && (
        <div className="mt-8 rounded-2xl border border-border bg-card p-5" data-testid="platforms-panel">
          <h2 className="font-ui mb-1 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Platforms · {rangeLabel}</h2>
          <p className="font-ui mb-4 text-xs text-muted-foreground">Unique visitors by device — anonymous, no device details stored.</p>
          {(() => {
            const total = data.platforms.reduce((a, p) => a + p.count, 0) || 1;
            return (
              <div className="space-y-3" data-testid="platforms-list">
                {data.platforms.map((p) => {
                  const pct = Math.round((p.count / total) * 100);
                  return (
                    <div key={p.platform} data-testid={`platform-${p.platform.replace(/\s+/g, "-")}`}>
                      <div className="mb-1 flex items-center justify-between">
                        <span className="font-ui text-sm">{p.platform}</span>
                        <span className="font-ui text-sm font-bold text-primary">{p.count.toLocaleString()} · {pct}%</span>
                      </div>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
